import type { CSSProperties, ReactElement, ReactNode } from "react";
import { IconPhoto } from "@tabler/icons-react";
import { Cbutton } from "../Cbutton";
import { Ccontainer } from "../Ccontainer";
import { Ccontainerskeleton } from "../Ccontainerskeleton";
import { Cimage, CIMAGE_ANIMATION, CIMAGE_OBJECT_FIT, type CimageObjectFit } from "../Cimage";
import { Ctext } from "../Ctext";
import { ButtonPreset, ContainerPreset, TextPreset } from "@/constants/componentPresets";
import { ColorType, getColorValue } from "@/theme/colors";
import { getRadiusCssValue } from "@/theme/radius";
import { pxToRem } from "@/theme/spacing";
import { IMAGE_PREVIEW_BLOOM_BACKDROP_STYLE, IMAGE_PREVIEW_FALLBACK_ALT_LABEL, IMAGE_PREVIEW_FALLBACK_GAP, IMAGE_PREVIEW_FALLBACK_ICON_OPACITY, IMAGE_PREVIEW_FALLBACK_ICON_SIZE, IMAGE_PREVIEW_FALLBACK_PADDING, IMAGE_PREVIEW_FALLBACK_TEXT_OPACITY, IMAGE_PREVIEW_FALLBACK_TEXT_SIZE, IMAGE_PREVIEW_REFERRER_POLICY, IMAGE_PREVIEW_THUMB_RADIUS, } from "./image-preview-constants";
import type { ImagePreviewFrame } from "./image-preview-frame";
import { ImagePreviewStatus } from "./image-preview-state";
const IMAGE_PREVIEW_FULL_SIZE = "100%";
interface CimagepreviewThumbnailProps {
    src: string;
    alt: string;
    title?: string;
    size: number;
    className?: string;
    style?: CSSProperties;
    status: ImagePreviewStatus;
    clickable: boolean;
    onClick: () => void;
    onError: () => void;
    /** Optional framing recipe; omitted → the shipped square ModalCard preview. */
    frame?: ImagePreviewFrame;
}
interface CimagepreviewContentProps {
    src: string;
    alt: string;
    title?: string;
    status: ImagePreviewStatus;
    onError: () => void;
    radius: number;
    objectFit: CimageObjectFit;
    bloomBackdrop: boolean;
}
interface CimagepreviewFallbackProps {
    alt: string;
}
interface CimagepreviewFrameProps {
    frame: ImagePreviewFrame | undefined;
    radius: number;
    children: ReactNode;
}
/** Fixed square by default; a frame `aspectRatio` makes the box fill its host
 *  width and derive its height, which is what the full-bleed variants need. */
function resolveThumbnailBoxStyle({ frame, size, }: {
    frame: ImagePreviewFrame | undefined;
    size: number;
}): CSSProperties {
    if (frame?.aspectRatio != null) {
        return { width: IMAGE_PREVIEW_FULL_SIZE, aspectRatio: String(frame.aspectRatio) };
    }
    return { width: pxToRem(size), height: pxToRem(size) };
}
export function CimagepreviewThumbnail({ src, alt, title, size, className, style, status, clickable, onClick, onError, frame, }: CimagepreviewThumbnailProps): ReactElement {
    const radius = frame?.radius ?? IMAGE_PREVIEW_THUMB_RADIUS;
    return (
    <Cbutton
      rootClassName={className}
      preset={ButtonPreset.Ghost}
      ariaLabel={clickable ? `Preview ${alt || "image"}` : undefined}
      padding={0}
      radius={radius}
      onClick={clickable ? onClick : undefined}
      disableHoverEffects={!clickable}
      style={{
            ...resolveThumbnailBoxStyle({ frame, size }),
            flexShrink: 0,
            ...style,
        }}
      containerStyle={{ width: IMAGE_PREVIEW_FULL_SIZE, height: IMAGE_PREVIEW_FULL_SIZE }}
    >
      <CimagepreviewFrame frame={frame} radius={radius}>
        <CimagepreviewContent src={src} alt={alt} title={title} status={status} onError={onError} radius={radius} objectFit={frame?.objectFit ?? CIMAGE_OBJECT_FIT.Cover} bloomBackdrop={frame?.bloomBackdrop === true}/>
      </CimagepreviewFrame>
    </Cbutton>
    );
}
/** The frame is either the shipped ModalCard container or, for `bare` recipes,
 *  a plain box whose whole surface comes from `coreStyle`. Either one can sit
 *  inside a nested shell tray. */
function CimagepreviewFrame({ frame, radius, children, }: CimagepreviewFrameProps): ReactElement {
    const coreStyle: CSSProperties = {
        position: "relative",
        overflow: "hidden",
        ...frame?.coreStyle,
    };
    const core = frame?.bare === true ? (<div style={{
            width: IMAGE_PREVIEW_FULL_SIZE,
            height: IMAGE_PREVIEW_FULL_SIZE,
            borderRadius: getRadiusCssValue(radius),
            ...coreStyle,
        }}>
        {children}
      </div>) : (<Ccontainer preset={ContainerPreset.ModalCard} padding={0} radius={radius} width={IMAGE_PREVIEW_FULL_SIZE} height={IMAGE_PREVIEW_FULL_SIZE} style={coreStyle}>
      {children}
    </Ccontainer>);
    if (!frame?.shell) {
        return core;
    }
    return (<div style={{
            width: IMAGE_PREVIEW_FULL_SIZE,
            height: IMAGE_PREVIEW_FULL_SIZE,
            padding: pxToRem(frame.shell.padding),
            borderRadius: getRadiusCssValue(frame.shell.radius),
            boxSizing: "border-box",
            ...frame.shell.style,
        }}>
      {core}
    </div>);
}
function CimagepreviewContent({ src, alt, title, status, onError, radius, objectFit, bloomBackdrop, }: CimagepreviewContentProps): ReactElement {
    if (status === ImagePreviewStatus.Loading) {
        return (<Ccontainerskeleton dataElScope="Cimagepreview/CimagepreviewThumbnail" width={IMAGE_PREVIEW_FULL_SIZE} height={IMAGE_PREVIEW_FULL_SIZE} radius={radius}/>);
    }
    if (status === ImagePreviewStatus.Broken) {
        return <CimagepreviewFallback alt={alt}/>;
    }
    return (<>
      {bloomBackdrop ? (<Cimage src={src} alt={alt} ariaHidden width={IMAGE_PREVIEW_FULL_SIZE} height={IMAGE_PREVIEW_FULL_SIZE} objectFit={CIMAGE_OBJECT_FIT.Cover} animation={CIMAGE_ANIMATION.None} referrerPolicy={IMAGE_PREVIEW_REFERRER_POLICY} style={IMAGE_PREVIEW_BLOOM_BACKDROP_STYLE}/>) : null}
      <Cimage src={src} alt={alt} title={title} width={IMAGE_PREVIEW_FULL_SIZE} height={IMAGE_PREVIEW_FULL_SIZE} objectFit={objectFit} showSkeleton={true} skeletonRadius={radius} referrerPolicy={IMAGE_PREVIEW_REFERRER_POLICY} onError={onError} style={{ position: "absolute", inset: 0, display: "block" }}/>
    </>);
}
function CimagepreviewFallback({ alt, }: CimagepreviewFallbackProps): ReactElement {
    return (
      <div style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: pxToRem(IMAGE_PREVIEW_FALLBACK_GAP),
            padding: pxToRem(IMAGE_PREVIEW_FALLBACK_PADDING),
        }}>
        <IconPhoto size={IMAGE_PREVIEW_FALLBACK_ICON_SIZE} color={getColorValue(ColorType.White, IMAGE_PREVIEW_FALLBACK_ICON_OPACITY)}/>
        <Ctext preset={TextPreset.Mild} size={IMAGE_PREVIEW_FALLBACK_TEXT_SIZE} opacity={IMAGE_PREVIEW_FALLBACK_TEXT_OPACITY} align="center">
          {alt || IMAGE_PREVIEW_FALLBACK_ALT_LABEL}
        </Ctext>
      </div>
    );
}
